// The layer array the map draws from.
//
// A layer is the only unit of grouping here: it names itself to readers, carries
// the icon and colour its points are drawn with, and declares whether its points
// are dated. The CMS authors it once on the parent Watchtower page, so
// everything below reads that one array rather than deriving a second list.

import { snakeToCamel } from '../../../../utils/caseconverters';

/**
 * Normalise the serialized layer configuration into what the map needs.
 *
 * Drops layers with no match value, and keeps only the first of any two that
 * claim the same one — the match value names a layer's Mapbox source and
 * layers, so an editor claiming a value twice would otherwise collide on
 * those ids and take the map down.
 */
export const toLayerConfig = (hybridActivity) => {
    const { dataset, layerColumn, layers } = hybridActivity || {};
    const claimed = new Set();
    const configured = [];

    (layers || []).forEach((layer) => {
        const matchValue = layer?.matchValue;

        if (!matchValue) {
            console.warn(
                `Hybrid activity: the layer "${
                    layer?.name || 'with no name'
                }" claims no data value, so nothing is drawn for it.`
            );
            return;
        }

        if (claimed.has(matchValue)) {
            console.warn(
                `Hybrid activity: two layers claim "${matchValue}". Using the first.`
            );
            return;
        }

        claimed.add(matchValue);
        configured.push(layer);
    });

    return {
        dataset: dataset || null,
        // Dataset rows arrive through keysToCamelFromSnake like every other
        // payload, so a column named layer_type reaches a feature as layerType.
        column: snakeToCamel(layerColumn || ''),
        layers: configured,
    };
};

/**
 * The layer array everything on screen reads: the picker and the map.
 *
 * A layer is identified by the value it claims, which `toLayerConfig` has already
 * made unique. Visibility is expressed as the layers a reader has switched off,
 * so a layer the editor adds later arrives visible without anything having to
 * notice it appeared.
 */
export const withVisibility = (config, hiddenIds) =>
    config.layers.map((layer) => ({
        ...layer,
        id: layer.matchValue,
        isVisible: !hiddenIds.has(layer.matchValue),
    }));

/**
 * The points belonging to layers that are currently switched on.
 *
 * Filtering the features rather than the Mapbox layer keeps one expression of
 * what is drawn — a Mapbox filter would be a second place a layer's visibility
 * is written down.
 */
export const selectVisiblePoints = (points, layers, column) => {
    if (!points) {
        return null;
    }

    const visible = new Set(
        layers
            .filter((layer) => layer.isVisible)
            .map((layer) => layer.matchValue)
    );

    return {
        type: 'FeatureCollection',
        features: points.features.filter((feature) =>
            visible.has(feature.properties?.[column])
        ),
    };
};

/**
 * The points a configured layer claims, and the values no layer claimed.
 *
 * Unclaimed rows are dropped rather than given a catch-all layer: a layer is
 * what a reader toggles, so a point outside every
 * layer would be one nothing on screen accounts for. The values they carried
 * come back so an editor can be told which ones went, rather than losing them
 * silently.
 */
export const selectConfiguredPoints = (points, config) => {
    const claimed = new Set(config.layers.map((layer) => layer.matchValue));
    const unclaimed = new Set();

    const features = (points?.features || []).filter((feature) => {
        const value = feature.properties?.[config.column];

        if (claimed.has(value)) {
            return true;
        }

        unclaimed.add(value || '');
        return false;
    });

    return {
        points: { type: 'FeatureCollection', features },
        unclaimedValues: [...unclaimed].sort(),
    };
};
